/**
 * Bodies panel — the visible solid bodies of the evaluated model.
 * One row per unconsumed body result: owning feature, triangle count,
 * suppress toggle, click to select. Consumed (boolean-absorbed) results and
 * rolled-back features don't appear — this is "what exists right now".
 */

import type { App } from '../app';
import { featureIcon } from './icons';

export function mountBodiesPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  const render = (): void => {
    panel.style.display = app.activePanel === 'bodies' ? 'block' : 'none';
    if (app.activePanel !== 'bodies') return;
    panel.innerHTML = '<h3>Bodies</h3>';

    const report = app.doc.lastReport;
    const rows: HTMLElement[] = [];
    if (report) {
      for (const [id, result] of report.results) {
        if (result.kind !== 'body' || report.consumed.has(id)) continue;
        if (result.mesh.positions.length === 0) continue;
        const feature = app.doc.timeline.get(id);
        if (!feature) continue;

        const row = document.createElement('div');
        row.className = 'body-row' + (app.selected === id ? ' selected' : '');
        row.innerHTML =
          `${featureIcon(feature.type)}<span class="body-name">${feature.name}</span>` +
          `<span class="body-tris">${(result.mesh.indices.length / 3).toLocaleString()} tris</span>`;
        row.addEventListener('click', () => app.select(id));

        const eye = document.createElement('button');
        eye.className = 'body-eye';
        eye.title = feature.suppressed ? 'Unsuppress' : 'Suppress';
        eye.textContent = feature.suppressed ? '◌' : '●';
        eye.addEventListener('click', (e) => {
          e.stopPropagation();
          void app.setSuppressed(id, !feature.suppressed);
        });
        row.appendChild(eye);
        rows.push(row);
      }
      // Suppressed body features (no result) still deserve a row to restore.
      for (const feature of app.doc.timeline.features) {
        if (!feature.suppressed) continue;
        if (!['extrude', 'revolve', 'sweep', 'import-mesh'].includes(feature.type)) continue;
        const row = document.createElement('div');
        row.className = 'body-row suppressed';
        row.innerHTML = `${featureIcon(feature.type)}<span class="body-name">${feature.name}</span>`;
        const eye = document.createElement('button');
        eye.className = 'body-eye';
        eye.title = 'Unsuppress';
        eye.textContent = '◌';
        eye.addEventListener('click', (e) => {
          e.stopPropagation();
          void app.setSuppressed(feature.id, false);
        });
        row.appendChild(eye);
        rows.push(row);
      }
    }

    if (rows.length === 0) {
      panel.insertAdjacentHTML('beforeend', '<div class="side-status">No bodies yet</div>');
    } else {
      panel.append(...rows);
    }
  };

  app.onUI(render);
  render();
}
